import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Checkbox } from '@/components/atoms/checkbox';
import { PublicCenteredPage } from '@/components/public/PublicCenteredPage';

/**
 * NOT-272 — public, no-auth customer page for a change order. Reached via the
 * short link delivered over email/SMS. The customer can review the change and
 * approve or decline it. Tenant context is established by the public GET
 * response and echoed back as `x-tenant-id` on the approve/decline calls
 * (mirrors the public estimate quote page).
 */
export const Route = createFileRoute('/(public)/change-order/$id')({
  component: PublicChangeOrderPage,
});

const OPS_URL = import.meta.env.VITE_OPERATIONS_URL ?? '';

interface LineItem {
  name?: string;
  lineTotal?: number;
  quantity?: number;
}

interface ModifiedLineItem {
  updated?: LineItem;
  original?: LineItem;
}

interface PublicChangeOrder {
  id: string;
  tenantId: string;
  changeOrderNumber: number;
  status: string;
  reason?: string | null;
  description?: string | null;
  priceImpact?: { subtotalDelta?: number; taxDelta?: number; totalDelta?: number } | null;
  lineItemsAdded?: LineItem[] | null;
  lineItemsRemovedSnapshot?: LineItem[] | null;
  lineItemsModified?: ModifiedLineItem[] | null;
  customer?: { name?: string; email?: string | null; phone?: string | null } | null;
  company?: { name?: string; phone?: string; email?: string; logoUrl?: string } | null;
  job?: { id: string; name?: string | null; jobNumber?: string | null } | null;
}

function money(n: number | undefined | null): string {
  const v = typeof n === 'number' ? n : 0;
  return (v < 0 ? '-$' : '$') + Math.abs(v).toFixed(2);
}

async function fetchChangeOrder(id: string): Promise<PublicChangeOrder> {
  const res = await fetch(`${OPS_URL}/change-orders/public/${id}`, {
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new Error('Change order not found');
  return (await res.json()) as PublicChangeOrder;
}

/**
 * The SPA and the API deploy separately, so a customer can be sitting on a tab
 * that predates a change to the request contract (the acceptance field being
 * the case in point). Their next click 400s through no fault of theirs, and
 * "please try again" is the one instruction that cannot work — the stale bundle
 * will send the same body. Tell them to reload instead.
 */
export const STALE_PAGE_MESSAGE = 'This page is out of date. Please refresh and try again.';

/** Exported for tests — the request contract is the thing under test. */
export async function postDecision(
  id: string,
  tenantId: string,
  action: 'customer-approve' | 'reject',
  body: Record<string, unknown>,
): Promise<void> {
  const res = await fetch(`${OPS_URL}/change-orders/public/${id}/${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-tenant-id': tenantId },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    if (res.status === 400) {
      throw new Error(STALE_PAGE_MESSAGE);
    }
    const message = await res
      .json()
      .then((d: { message?: string }) => d?.message)
      .catch(() => undefined);
    throw new Error(message || 'Request failed');
  }
}

function PublicChangeOrderPage() {
  const { id } = Route.useParams();
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const [done, setDone] = useState<'approved' | 'declined' | null>(null);
  // Approving re-prices the job, so — as on the public estimate — the tick is
  // the record that the customer accepted the new total, not just clicked.
  const [accepted, setAccepted] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery<PublicChangeOrder>({
    queryKey: ['public-change-order', id],
    queryFn: () => fetchChangeOrder(id),
  });

  const approve = useMutation({
    // The server refuses the approval without `termsAccepted` and records when
    // it was given — the disabled CTA and the page-level guard are the UX in
    // front of that, not the enforcement.
    mutationFn: () => postDecision(id, data!.tenantId, 'customer-approve', { termsAccepted: true }),
    onSuccess: () => {
      setDone('approved');
      void refetch();
    },
  });
  const decline = useMutation({
    mutationFn: () =>
      postDecision(id, data!.tenantId, 'reject', { reason: reason.trim() || undefined }),
    onSuccess: () => {
      setDone('declined');
      void refetch();
    },
  });

  // Mirrors handleApprove/handleSelect on the estimate and proposal pages: the
  // disabled CTA is the UX, this is the guard. Keeps the acceptance requirement
  // attached to the mutation rather than to one button's disabled prop.
  const handleApprove = () => {
    if (!accepted) return;
    approve.mutate();
  };

  if (isLoading) {
    return <Centered>Loading…</Centered>;
  }
  if (isError || !data) {
    return <Centered>This change order link is invalid or has expired.</Centered>;
  }

  const total = money(data.priceImpact?.totalDelta);
  const pending = data.status === 'PENDING_APPROVAL' && !done;
  const decided =
    done ??
    (data.status === 'APPROVED' || data.status === 'APPLIED'
      ? 'approved'
      : data.status === 'REJECTED'
        ? 'declined'
        : null);
  const added = data.lineItemsAdded ?? [];
  const removed = data.lineItemsRemovedSnapshot ?? [];
  const modified = (data.lineItemsModified ?? [])
    .map((m) => m.updated ?? m.original)
    .filter((li): li is LineItem => Boolean(li));

  return (
    <PublicCenteredPage className="bg-gray-100 font-sans">
      <div className="mx-auto max-w-xl overflow-hidden rounded-xl bg-white shadow">
        <div className="border-b px-6 py-5">
          <p className="break-words text-lg font-bold text-gray-900">
            {data.company?.name ?? 'Your company'}
          </p>
          <p className="text-sm text-gray-500">
            Change Order CO-{data.changeOrderNumber}
            {data.job?.jobNumber ? ` · Job ${data.job.jobNumber}` : ''}
          </p>
        </div>

        <div className="px-6 py-5">
          <p className="mb-1 text-sm text-gray-500">Net change to your job total</p>
          <p className="text-3xl font-bold text-gray-900">{total}</p>

          {data.reason ? (
            <div className="mt-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Reason</p>
              <p className="text-sm text-gray-700">{data.reason}</p>
            </div>
          ) : null}

          <ItemList title="Added" items={added} />
          <ItemList title="Removed" items={removed} negative />
          <ItemList title="Updated" items={modified} />
        </div>

        <div className="border-t px-6 py-5">
          <ChangeOrderDecision
            decided={decided}
            pending={pending}
            declining={declining}
            reason={reason}
            onReasonChange={setReason}
            accepted={accepted}
            onAcceptedChange={setAccepted}
            approving={approve.isPending}
            decliningPending={decline.isPending}
            onStartDecline={() => setDeclining(true)}
            onCancelDecline={() => setDeclining(false)}
            onApprove={handleApprove}
            onConfirmDecline={() => decline.mutate()}
          />
          {(approve.isError || decline.isError) && !done ? (
            <p className="mt-3 text-center text-sm text-red-600">
              {approve.error?.message === STALE_PAGE_MESSAGE ||
              decline.error?.message === STALE_PAGE_MESSAGE
                ? STALE_PAGE_MESSAGE
                : 'Something went wrong. Please try again.'}
            </p>
          ) : null}
        </div>

        <div className="border-t px-6 py-4">
          <p className="break-words text-xs text-gray-400">
            {data.company?.name ?? 'Your company'}
            {data.company?.phone ? ` · ${data.company.phone}` : ''}
            {data.company?.email ? ` · ${data.company.email}` : ''}
          </p>
        </div>
      </div>
    </PublicCenteredPage>
  );
}

/**
 * The decide panel. Exported so the acceptance gate is testable without a
 * router: the TanStack router plugin rewrites route components in the vitest
 * pipeline, so the page itself can't be rendered directly (same reason
 * quote.$id.tsx exports EstimateView).
 */
export function ChangeOrderDecision({
  decided,
  pending,
  declining,
  reason,
  onReasonChange,
  accepted,
  onAcceptedChange,
  approving,
  decliningPending,
  onStartDecline,
  onCancelDecline,
  onApprove,
  onConfirmDecline,
}: {
  decided: 'approved' | 'declined' | null;
  pending: boolean;
  declining: boolean;
  reason: string;
  onReasonChange: (value: string) => void;
  accepted: boolean;
  onAcceptedChange: (accepted: boolean) => void;
  approving: boolean;
  decliningPending: boolean;
  onStartDecline: () => void;
  onCancelDecline: () => void;
  onApprove: () => void;
  onConfirmDecline: () => void;
}) {
  if (decided === 'approved') {
    return (
      <p className="text-center font-medium text-emerald-700">
        ✓ You approved this change order. Thank you!
      </p>
    );
  }
  if (decided === 'declined') {
    return <p className="text-center font-medium text-gray-600">You declined this change order.</p>;
  }
  if (!pending) {
    return (
      <p className="text-center text-sm text-gray-500">
        This change order is not currently awaiting your approval.
      </p>
    );
  }
  if (declining) {
    return (
      <div>
        <label className="mb-1 block text-sm text-gray-600">Reason for declining (optional)</label>
        <textarea
          className="mb-3 w-full rounded-md border px-3 py-2 text-sm"
          rows={3}
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
        />
        <div className="flex gap-3">
          <button
            className="flex-1 rounded-md border px-4 py-2 text-sm font-medium text-gray-700"
            onClick={onCancelDecline}
            disabled={decliningPending}
          >
            Back
          </button>
          <button
            className="flex-1 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            onClick={onConfirmDecline}
            disabled={decliningPending}
          >
            {decliningPending ? 'Submitting…' : 'Confirm decline'}
          </button>
        </div>
      </div>
    );
  }
  return (
    <div>
      <label className="mb-4 flex cursor-pointer items-start gap-2 text-sm text-gray-700">
        <Checkbox
          checked={accepted}
          onCheckedChange={(v) => onAcceptedChange(v === true)}
          className="mt-0.5"
        />
        <span>
          By approving, you accept this change order and the resulting change to your job total.
        </span>
      </label>
      <div className="flex gap-3">
        {/* Declining is never gated — only the commitment is. */}
        <button
          className="flex-1 rounded-md border px-4 py-2 text-sm font-medium text-gray-700"
          onClick={onStartDecline}
          disabled={approving}
        >
          Decline
        </button>
        <button
          className="flex-1 rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          onClick={onApprove}
          disabled={approving || !accepted}
        >
          {approving ? 'Submitting…' : 'Approve change order'}
        </button>
      </div>
    </div>
  );
}

function ItemList({
  title,
  items,
  negative,
}: {
  title: string;
  items: LineItem[];
  negative?: boolean;
}) {
  if (!items.length) return null;
  return (
    <div className="mt-4">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
        {title} items
      </p>
      <ul className="divide-y rounded-md border">
        {items.map((li, i) => (
          <li key={i} className="flex justify-between px-3 py-2 text-sm">
            <span className="text-gray-700">{li.name ?? 'Item'}</span>
            <span className="text-gray-900">
              {negative && typeof li.lineTotal === 'number'
                ? money(-Math.abs(li.lineTotal))
                : money(li.lineTotal)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <PublicCenteredPage className="bg-gray-100 text-center text-gray-600">
      {children}
    </PublicCenteredPage>
  );
}
